import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPost } from "../api/client";
import type {
  PlayState,
  StepOwnerType,
  StepState,
  StepType,
} from "./usePlay";

// Mirrors the /api/me/steps response item from apps/api/src/routes/meInbox.ts
export interface InboxStepItem {
  step: {
    id: string;
    playId: string;
    ord: number;
    type: StepType;
    title: string;
    state: StepState;
    ownerType: StepOwnerType;
    ownerUserId: string | null;
    signoffUserId: string | null;
    requiresSignoff: boolean;
    claimedAt: string | null;
    startedAt: string | null;
    completedAt: string | null;
  };
  play: {
    id: string;
    archetype: string;
    state: PlayState;
    workspaceId: string;
    workspaceName: string;
    targetWindowEnd: string | null;
  };
}

export interface InboxStepsResponse {
  items: InboxStepItem[];
}

export interface MentionItem {
  comment: {
    id: string;
    body: string;
    createdAt: string;
    editedAt: string | null;
  };
  author: { id: string; email: string; displayName: string };
  play: {
    id: string;
    archetype: string;
    state: PlayState;
    workspaceId: string;
    workspaceName: string;
  };
  step: { id: string; title: string; state: StepState } | null;
}

export interface MentionsResponse {
  items: MentionItem[];
}

export type CardType =
  | "review"
  | "decision"
  | "confirmation"
  | "intervention"
  | "milestone";

export type CardState =
  | "open"
  | "actioned"
  | "auto_actioned"
  | "expired"
  | "withdrawn";

export interface CardItem {
  id: string;
  workspaceId: string;
  playId: string;
  stepId: string;
  type: CardType;
  state: CardState;
  criticality: "high" | "medium" | "low";
  body: Record<string, unknown>;
  createdAt: string;
  expiresAt: string | null;
}

export interface CardsResponse {
  cards: CardItem[];
  total: number;
}

export type OperatorTab =
  | "today"
  | "mine"
  | "claimable"
  | "review"
  | "completed"
  | "mentions"
  | "cards";

// Default visible states for "My steps". Excludes terminal states.
const MINE_STATES = "ready,running,awaiting_review,paused";

/**
 * Steps assigned to the current user (or claimable rows the API surfaces).
 * The default API filter already returns claimable rows alongside owned rows,
 * so this hook simply asks for actionable states and the caller filters
 * client-side as needed.
 */
export function useMyOperatorSteps() {
  return useQuery<InboxStepsResponse, Error>({
    queryKey: ["me-steps", "mine"],
    queryFn: () =>
      apiGet<InboxStepsResponse>(`/api/me/steps?state=${MINE_STATES}`),
    staleTime: 30_000,
  });
}

/**
 * Awaiting-my-review steps (signoff_user_id = me, state = awaiting_review).
 */
export function useReviewerSteps() {
  return useQuery<InboxStepsResponse, Error>({
    queryKey: ["me-steps", "reviewer"],
    queryFn: () =>
      apiGet<InboxStepsResponse>(
        `/api/me/steps?state=awaiting_review&reviewer=true`,
      ),
    staleTime: 30_000,
  });
}

/**
 * Recently completed steps the operator owned. Filters client-side because the
 * default endpoint scope is "actionable" — completed is requested explicitly.
 */
export function useRecentlyCompletedSteps(limit = 20) {
  return useQuery<InboxStepsResponse, Error>({
    queryKey: ["me-steps", "completed", limit],
    queryFn: () =>
      apiGet<InboxStepsResponse>(`/api/me/steps?state=completed&limit=${limit}`),
    staleTime: 30_000,
  });
}

export function useMyMentions(limit = 50) {
  return useQuery<MentionsResponse, Error>({
    queryKey: ["me-mentions", limit],
    queryFn: () => apiGet<MentionsResponse>(`/api/me/mentions?limit=${limit}`),
    staleTime: 30_000,
  });
}

export function useMyCards(workspaceId: string | null | undefined) {
  return useQuery<CardsResponse, Error>({
    queryKey: ["cards-mine", workspaceId],
    queryFn: () =>
      apiGet<CardsResponse>(`/api/workspaces/${workspaceId}/cards/mine`),
    enabled: !!workspaceId,
    staleTime: 30_000,
  });
}

/**
 * Cross-play step mutations used from HandsOps. These hit the workspace-agnostic
 * `/api/steps/:id/*` endpoints (same routes the in-play StepDetail uses) and
 * invalidate every me-steps key on success.
 */
export function useOperatorStepMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["me-steps"] });
    qc.invalidateQueries({ queryKey: ["me-mentions"] });
  };

  const claim = useMutation({
    mutationFn: (stepId: string) => apiPost(`/api/steps/${stepId}/claim`, {}),
    onSuccess: invalidate,
  });
  const complete = useMutation({
    mutationFn: (args: { stepId: string; output: Record<string, unknown> }) =>
      apiPost(`/api/steps/${args.stepId}/complete`, { output: args.output }),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: (args: { stepId: string; reason: string }) =>
      apiPost(`/api/steps/${args.stepId}/reject`, { reason: args.reason }),
    onSuccess: invalidate,
  });

  const actionCard = useMutation({
    mutationFn: (args: { workspaceId: string; cardId: string; action: string }) =>
      apiPost(`/api/workspaces/${args.workspaceId}/cards/${args.cardId}/action`, {
        action: args.action,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["cards-mine"] });
    },
  });

  return { claim, complete, reject, actionCard };
}

/**
 * Split the mine-queue items into "owned by me" vs "claimable" so the UI can
 * surface them on separate tabs without a second round-trip.
 */
export function partitionMineAndClaimable(
  items: InboxStepItem[],
  myUserId: string | null | undefined,
): { mine: InboxStepItem[]; claimable: InboxStepItem[] } {
  const mine: InboxStepItem[] = [];
  const claimable: InboxStepItem[] = [];
  for (const it of items) {
    if (myUserId && it.step.ownerUserId === myUserId) {
      mine.push(it);
    } else if (it.step.ownerUserId === null) {
      claimable.push(it);
    } else {
      // Owned by someone else but surfaced — skip (shouldn't happen given API).
    }
  }
  return { mine, claimable };
}
